import React, { useCallback, useEffect, useState } from 'react';
import { CheckCircleFilled, ClockCircleOutlined, UserAddOutlined } from '@ant-design/icons';
import { getStudentInvite, toEpochMs, type StudentInviteState } from '../../api/invite';
import { InviteStudentModal } from './InviteStudentModal';

interface StudentInviteSectionProps {
  studentId: string;
  studentName?: string;
}

// Why the last invite did not lead to access, shown above the invite button.
const PREVIOUS_OUTCOME: Record<string, string> = {
  EXPIRED: 'The last invite expired before it was used.',
  REVOKED: 'The last invite was cancelled.',
  FAILED: "We couldn't reach the last number on WhatsApp.",
  LOCKED: 'The last invite was locked after too many attempts.',
};

const formatExpiry = (ms?: number) =>
  ms
    ? new Date(ms).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })
    : '';

/**
 * Student app access on the dashboard card (ZAP-74): "Invite" when the student has never been
 * invited or the last invite did not work out, "Invited · pending" with a resend, or
 * "has app access" once they have signed in.
 */
export const StudentInviteSection: React.FC<StudentInviteSectionProps> = ({ studentId, studentName }) => {
  const [state, setState] = useState<StudentInviteState | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const firstName = studentName?.split(' ')[0] || 'Student';

  // Without the status, still let the parent invite; the API refuses a duplicate.
  const fetchState = useCallback(
    () => getStudentInvite(studentId).catch((): StudentInviteState | null => null),
    [studentId],
  );

  const reload = useCallback(() => {
    fetchState().then(setState);
  }, [fetchState]);

  useEffect(() => {
    let cancelled = false;
    fetchState().then((next) => {
      if (!cancelled) setState(next);
    });
    return () => {
      cancelled = true;
    };
  }, [fetchState]);

  if (state?.appAccess === 'ACTIVE') {
    return (
      <div className="sc__invite-status sc__invite-status--active">
        <CheckCircleFilled /> {firstName} has app access
      </div>
    );
  }

  const isPending = state?.appAccess === 'INVITED';
  const expiresAt = toEpochMs(state?.expiresAt);
  const note =
    state?.appAccess === 'DISABLED'
      ? `${firstName}'s app access was removed. Send a new invite to restore it.`
      : state?.status
        ? PREVIOUS_OUTCOME[state.status]
        : undefined;

  return (
    <>
      {isPending && (
        <div className="sc__invite-status sc__invite-status--pending">
          <ClockCircleOutlined /> Invited · pending
          <span className="sc__invite-status-sub">
            Sent on WhatsApp to {state?.maskedPhone}
            {expiresAt ? ` · expires ${formatExpiry(expiresAt)}` : ''}
          </span>
        </div>
      )}
      {!isPending && note && <div className="sc__invite-note">{note}</div>}

      <button className="sc__invite-btn" onClick={() => setModalOpen(true)}>
        <UserAddOutlined style={{ fontSize: 14 }} /> {isPending ? 'Resend invite' : `Invite ${firstName} to the app`}
      </button>

      <InviteStudentModal
        visible={modalOpen}
        studentId={studentId}
        studentName={studentName}
        isResend={isPending}
        onClose={() => setModalOpen(false)}
        onSent={reload}
      />
    </>
  );
};
